import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';

import { Drawer } from '../Drawer/Drawer';
import { DrawerActionBar } from '../Drawer/DrawerActionBar';
import { DrawerHeader } from '../Drawer/DrawerHeader';
import { DiscardChangesModal } from './components/DiscardChangesModal';
import { LeftPanel } from './components/LeftPanel';
import { RightPanel } from './components/RightPanel';
import { DRAWER_CONTAINER_STYLES, DRAWER_HEADER_STYLES, TWO_PANEL_CONTAINER_STYLES } from './constants';
import type { SelectionDrawerProps } from './types';

export const SelectionDrawer = <TItem, TSelected = TItem>({
  dataTest,
  title,
  open,
  leftPanel,
  rightPanel,
  actions,
  discardConfig,
  renderItem,
  renderSelectedItem,
  slots,
  drawerSx,
}: SelectionDrawerProps<TItem, TSelected>) => {
  const { t } = useTranslation();

  const drawerActions = [
    {
      label: actions.applyLabel ?? t('Apply'),
      onClick: actions.onApply,
      variant: 'contained' as const,
      color: 'primary' as const,
      disabled: actions.applyDisabled,
      'data-test': 'apply-button',
      'data-pendo-feature': actions.applyPendoFeature,
    },
    ...(actions.additionalActions ?? []),
    {
      label: actions.cancelLabel ?? t('Cancel'),
      onClick: actions.onClose,
      variant: 'text' as const,
      color: 'primary' as const,
      'data-test': 'cancel-button',
    },
  ];

  return (
    <>
      <Drawer data-test={dataTest} open={open} onClose={actions.onClose} sx={drawerSx}>
        <Box sx={DRAWER_CONTAINER_STYLES}>
          <DrawerHeader onClose={actions.onClose} dataTest="drawer-header" sx={DRAWER_HEADER_STYLES}>
            {title}
          </DrawerHeader>

          {slots?.body ?? (
            <>
              {slots?.leftPanelHeader}

              <Box sx={TWO_PANEL_CONTAINER_STYLES}>
                <LeftPanel {...leftPanel} renderItem={renderItem} singlePanel={!rightPanel} />

                {rightPanel !== undefined && <RightPanel {...rightPanel} renderSelectedItem={renderSelectedItem} />}
              </Box>

              {slots?.rightPanelHeader}
            </>
          )}

          <Box
            sx={{
              mt: 'auto',
              pt: 2,
              pb: 2,
              px: 3,
              borderTop: '1px solid',
              borderColor: 'divider',
              '& [data-test="action-bar"]': {
                justifyContent: 'flex-end',
              },
            }}
          >
            <DrawerActionBar
              actions={drawerActions}
              stickyBottom={false}
              gutterBottom={false}
              dataTest="drawer-action-bar"
            />
          </Box>
        </Box>
      </Drawer>

      {discardConfig && (
        <DiscardChangesModal
          show={discardConfig.show}
          onConfirm={discardConfig.onConfirm}
          onCancel={discardConfig.onCancel}
          title={discardConfig.title}
          message={discardConfig.message}
        />
      )}
    </>
  );
};
